// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Box, Divider, IconButton, Typography, useTheme } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { Release } from "../types/types";
import Markdown from "react-markdown";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/rootReducer";

const ReleaseDescription: React.FC<{
  release: Release | undefined;
  handleClose: () => void;
}> = ({ release, handleClose }) => {
  const theme = useTheme();
  const isDarkTheme: boolean = useSelector((state: RootState) => state.theme);
  const linkColor = isDarkTheme
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Box
      sx={{
        width: { xs: "92%", sm: "75%", md: "60%" },
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
        >
          {release?.name}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": { color: theme.palette.text.primary },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Markdown body */}
      <Box
        sx={{
          overflowY: "auto",
          px: 3,
          py: 2.5,
          color: theme.palette.text.primary,
          fontFamily: "Montserrat, sans-serif",
          fontSize: "0.875rem",
          lineHeight: 1.7,
          "& h1, & h2, & h3, & h4": {
            fontWeight: 700,
            marginTop: "1.25em",
            marginBottom: "0.4em",
            color: theme.palette.text.primary,
          },
          "& h1": { fontSize: "1.25rem" },
          "& h2": { fontSize: "1.125rem" },
          "& h3": { fontSize: "1rem" },
          "& p": { marginTop: 0, marginBottom: "0.75em" },
          "& ul, & ol": { paddingLeft: "1.5em", marginBottom: "0.75em" },
          "& li": { marginBottom: "0.3em" },
          "& a": {
            color: linkColor,
            textDecoration: "none",
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          },
          "& code": {
            fontFamily: "monospace",
            fontSize: "0.8125rem",
            backgroundColor: isDarkTheme
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.06)",
            borderRadius: "4px",
            px: "5px",
            py: "2px",
          },
          "& pre": {
            backgroundColor: isDarkTheme
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)",
            borderRadius: "8px",
            padding: "1em",
            overflowX: "auto",
            marginBottom: "1em",
            "& code": { backgroundColor: "transparent", px: 0, py: 0 },
          },
          "& hr": {
            border: "none",
            borderTop: `1px solid ${theme.palette.divider}`,
            my: "1em",
          },
          "& blockquote": {
            borderLeft: `3px solid ${theme.palette.divider}`,
            margin: 0,
            paddingLeft: "1em",
            color: theme.palette.text.secondary,
          },
        }}
      >
        <Markdown>{release?.body}</Markdown>
      </Box>
    </Box>
  );
};

export default ReleaseDescription;
